import { createApiClient } from './client'

export interface VehicleProfile {
  id: string
  user_id: string
  name?: string | null
  vehicle_type: 'truck' | 'suv' | 'other'
  make: string
  model: string
  year?: number
  is_primary: boolean
  is_daily_driver: boolean
  notes?: string
  // Equipment flags
  has_winch?: boolean
  has_skid_plates?: boolean
  has_rock_sliders?: boolean
  has_roof_rack?: boolean
  has_roof_top_tent?: boolean
  has_truck_camper?: boolean
  created_at: string
  updated_at: string
}

export interface CreateVehicleProfileDto {
  name?: string
  vehicle_type: 'truck' | 'suv' | 'other'
  make: string
  model: string
  year?: number
  is_primary?: boolean
  is_daily_driver?: boolean
  notes?: string
  // Equipment flags
  has_winch?: boolean
  has_skid_plates?: boolean
  has_rock_sliders?: boolean
  has_roof_rack?: boolean
  has_roof_top_tent?: boolean
  has_truck_camper?: boolean
}

export interface UpdateVehicleProfileDto {
  name?: string | null
  vehicle_type?: 'truck' | 'suv' | 'other'
  make?: string
  model?: string
  year?: number
  is_primary?: boolean
  is_daily_driver?: boolean
  notes?: string
  // Equipment flags
  has_winch?: boolean
  has_skid_plates?: boolean
  has_rock_sliders?: boolean
  has_roof_rack?: boolean
  has_roof_top_tent?: boolean
  has_truck_camper?: boolean
}

export interface VehicleMakeMetadata {
  id: number
  name: string
}

export interface VehicleModelMetadata {
  id: number
  name: string
  make: string
}

export interface VehicleApiResponse {
  success: boolean
  data: VehicleProfile
}

export interface VehicleListApiResponse {
  success: boolean
  data: VehicleProfile[]
}

interface VehicleMetadataResponse<T> {
  success: boolean
  data: T
}

export class VehicleApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Get all vehicles for the authenticated user
  async getVehicles(): Promise<VehicleProfile[]> {
    const response = await this.apiClient.get<VehicleListApiResponse>('/vehicles')
    return response.data
  }

  // Get another user's vehicles, for viewing their garage. Visitors don't receive notes.
  async getVehiclesForUser(userId: string): Promise<VehicleProfile[]> {
    const response = await this.apiClient.get<VehicleListApiResponse>(`/vehicles/users/${userId}`)
    return response.data
  }

  // Get vehicle by ID
  async getVehicle(vehicleId: string): Promise<VehicleProfile> {
    const response = await this.apiClient.get<VehicleApiResponse>(`/vehicles/${vehicleId}`)
    return response.data
  }

  // Create new vehicle profile
  async createVehicle(vehicleData: CreateVehicleProfileDto): Promise<VehicleProfile> {
    const response = await this.apiClient.post<VehicleApiResponse>('/vehicles', vehicleData)
    return response.data
  }

  // Update vehicle profile
  async updateVehicle(vehicleId: string, vehicleData: UpdateVehicleProfileDto): Promise<VehicleProfile> {
    const response = await this.apiClient.put<VehicleApiResponse>(`/vehicles/${vehicleId}`, vehicleData)
    return response.data
  }

  // Delete vehicle profile
  async deleteVehicle(vehicleId: string): Promise<void> {
    await this.apiClient.delete(`/vehicles/${vehicleId}`)
  }

  // Set primary vehicle
  async setPrimaryVehicle(vehicleId: string): Promise<VehicleProfile> {
    const response = await this.apiClient.patch<VehicleApiResponse>(`/vehicles/${vehicleId}/primary`)
    return response.data
  }

  async getVehicleMakes(vehicleType?: string): Promise<VehicleMakeMetadata[]> {
    const response = await this.apiClient.get<VehicleMetadataResponse<VehicleMakeMetadata[]>>(
      '/vehicles/metadata/makes',
      {
        vehicleType
      }
    )

    return response.data
  }

  async getVehicleModels(params: { make: string; year?: string }): Promise<VehicleModelMetadata[]> {
    const response = await this.apiClient.get<VehicleMetadataResponse<VehicleModelMetadata[]>>(
      '/vehicles/metadata/models',
      params
    )

    return response.data
  }
}
